/**
 * InvoiceDetailView — single invoice view with payment panel, modals.
 */

import { useEffect } from 'react'

import { PaymentPanel } from './InvoiceDetailView-parts'
import { calcDaysOverdue } from './invoices-helpers'
import { InvoiceView } from '../../components/billing'
import { useBaseCurrency } from '../../hooks/use-currencies'
import { getInvoice } from '../../services'
import { normalizePaymentStatus } from '../../utils/billing-helpers'

import type { InvoiceActionsState } from './useInvoiceActions'
import type { InvoicePaymentsResponse } from '../../services/payment-service'
import type { Invoice } from '../../types/billing'

interface Props {
  invoice: Invoice
  isVendor: boolean
  isProforma: boolean
  isLoading: boolean
  actions: InvoiceActionsState
  organizationId?: string
  currentLedgerId?: string | null
  fetchInvoices?: () => Promise<void>
}

interface InvoiceFlags {
  canEdit: boolean
  canPost: boolean
  canVoid: boolean
  isPosted: boolean
  isWrittenOff: boolean
}

function computeInvoiceFlags(invoice: Invoice): InvoiceFlags {
  const canEditPosted = invoice.status === 'posted' && !invoice.irn && !invoice.ewayBillNumber
  return {
    canEdit: invoice.status === 'draft' || canEditPosted,
    canPost: invoice.status === 'draft',
    canVoid: invoice.status === 'posted',
    isPosted: invoice.status === 'posted',
    isWrittenOff: normalizePaymentStatus(invoice.paymentStatus) === 'written_off',
  }
}

interface InvoiceTotals {
  invoiceCurrency: string
  totalAmount: number
  totalPaid: number
  outstandingBalance: number
  daysOverdue: number
  isOverdue: boolean
}

function computeInvoiceTotals(
  invoice: Invoice,
  baseCurrency: string,
  allocations: { allocatedAmount?: string | number | null }[],
  isWrittenOff: boolean,
): InvoiceTotals {
  const invoiceCurrency = invoice.currency ?? baseCurrency
  const totalAmount = Number(invoice.totalAmount ?? 0)
  const totalPaid = allocations.reduce((sum, a) => sum + (Number(a.allocatedAmount) || 0), 0)
  const outstandingBalance = totalAmount - totalPaid
  const daysOverdue = calcDaysOverdue(invoice.dueDate)
  const isOverdue = daysOverdue > 0 && !isWrittenOff && outstandingBalance > 0
  return { invoiceCurrency, totalAmount, totalPaid, outstandingBalance, daysOverdue, isOverdue }
}

function buildPaymentPanel(args: {
  invoice: Invoice
  isVendor: boolean
  isProforma: boolean
  flags: InvoiceFlags
  totals: InvoiceTotals
  allocations: InvoicePaymentsResponse['allocations']
  actions: InvoiceActionsState
}): JSX.Element {
  const { invoice, isVendor, isProforma, flags, totals, allocations, actions } = args
  return (
    <PaymentPanel
      allocations={allocations}
      confirmingAction={actions.confirmingAction}
      confirmTimerRef={actions.confirmTimerRef}
      daysOverdue={totals.daysOverdue}
      invoice={invoice}
      invoiceCurrency={totals.invoiceCurrency}
      isInvoicePaymentsLoading={actions.isInvoicePaymentsLoading}
      isOverdue={totals.isOverdue}
      isPosted={flags.isPosted}
      isProforma={isProforma}
      isVendor={isVendor}
      isWriteOffSaving={actions.isWriteOffSaving}
      isWrittenOff={flags.isWrittenOff}
      outstandingBalance={totals.outstandingBalance}
      setConfirmingAction={actions.setConfirmingAction}
      setIsWriteOffConfirmVisible={actions.setIsWriteOffConfirmVisible}
      setWriteOffType={actions.setWriteOffType}
      totalAmount={totals.totalAmount}
      totalPaid={totals.totalPaid}
      writeOffType={actions.writeOffType}
      onReverseWriteOff={() => { void actions.handleReverseWriteOff() }}
    />
  )
}

export function InvoiceDetailView({
  invoice, isVendor, isProforma, isLoading, actions,
}: Props): JSX.Element {
  const baseCurrency = useBaseCurrency()
  const { fetchInvoicePayments } = actions

  useEffect(() => {
    void fetchInvoicePayments(invoice.id)
  }, [fetchInvoicePayments, invoice.id])

  const flags = computeInvoiceFlags(invoice)
  const allocations = actions.invoicePayments?.allocations ?? []
  const totals = computeInvoiceTotals(invoice, baseCurrency, allocations, flags.isWrittenOff)

  const paymentPanelContent = buildPaymentPanel({
    invoice, isVendor, isProforma, flags, totals, allocations, actions,
  })

  return (
    <InvoiceView
      invoice={invoice}
      isLoading={isLoading}
      paymentPanel={paymentPanelContent}
      onBack={actions.handleBack}
      onClone={() => actions.handleClone(invoice)}
      onGeneratePDF={() => { void actions.handleGeneratePDF(invoice) }}
      onRecordPayment={invoice.partyId ? () => actions.setIsPaymentModalVisible(true) : undefined}
      onViewHistory={() => actions.setIsVersionHistoryVisible(true)}
      {...(flags.canEdit ? { onEdit: () => actions.handleEdit(invoice) } : {})}
      {...(flags.canPost ? { onPost: () => actions.handlePost(invoice) } : {})}
      {...(flags.canVoid ? { onVoid: () => actions.handleVoid(invoice) } : {})}
    />
  )
}

// ── Payment success handler factory ──────────────────────────────────────────

// eslint-disable-next-line max-params -- TODO(refactor): convert to options object
export function makePaymentSuccessHandler(
  setIsPaymentModalVisible: (v: boolean) => void,
  organizationId: string,
  invoice: Invoice,
  setSelectedInvoice: (inv: Invoice | null) => void,
  fetchInvoicePayments: (id: string) => Promise<void>,
  fetchInvoices: () => Promise<void>,
) {
  return () => {
    setIsPaymentModalVisible(false)
    void getInvoice(organizationId, invoice.id).then(invResp => {
      if (invResp.data) { setSelectedInvoice(invResp.data) }
    })
    void fetchInvoicePayments(invoice.id)
    void fetchInvoices()
  }
}
